import { useMemo, useState } from 'react';
import { uniq } from 'lodash-es';
import { AddressSearch, FilterDropdown } from '@ui';
import { CustomStartLocationType } from '@lp/features/RoutePlanner/types';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { SelectItemType } from '@/types';
import { StartLocationObjectType } from '@/api/apis/mission-planner';
import { convertHereLocation, getCountryIso2, getLocationAddress } from '@/utils/helpers';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';

type StartLocationCustomSelectProps = {
  data: CustomStartLocationType | null;
  countryCodes: string[];
  handleCustomAddressSelect: (data: CustomStartLocationType | null) => void;
};

export const StartLocationCustomSelect = ({
  data,
  countryCodes,
  handleCustomAddressSelect,
}: StartLocationCustomSelectProps) => {
  const [addressSearchValue, setAddressSearchValue] = useState(data?.customTitle || '');
  const [selectedCountry, setSelectedCountry] = useState(data?.location.country || 'US');
  const { countriesByIso2 } = useCountriesData();
  const selectedCountryIso2 = getCountryIso2(selectedCountry, countriesByIso2);

  const { list: addressList } = useAddressSearch(
    { address: addressSearchValue || '', countryCode: selectedCountryIso2 },
    !!selectedCountryIso2 && !!addressSearchValue,
  );

  const countriesList = useMemo(() => {
    return uniq(countryCodes.map((countryCode) => getCountryIso2(countryCode, countriesByIso2)))
      .map((countryCode) => ({
        value: countryCode,
        name: countriesByIso2[countryCode]?.name ?? countryCode,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) as SelectItemType[];
  }, [countryCodes, countriesByIso2]);

  const selectedCountryItem = useMemo(() => {
    return countriesList.find((country) => country.value === selectedCountryIso2);
  }, [countriesList, selectedCountryIso2]);

  const handleCountrySelect = (keys: string[]) => {
    setSelectedCountry(keys[0]);
  };

  const handleAddressSelect = (addressText: string) => {
    const addressItem = addressList.find((a) => a.id === addressText);

    if (addressItem) {
      const location = convertHereLocation(addressItem, countriesByIso2);
      const currentLocationString = getLocationAddress(location);

      handleCustomAddressSelect({
        type: StartLocationObjectType.Custom,
        id: addressItem.id,
        location,
        customTitle: currentLocationString,
      });

      setAddressSearchValue(currentLocationString);
    }
  };

  const handleAddressInputChange = (text: string) => {
    setAddressSearchValue(text);
    handleCustomAddressSelect(null);
  };

  const hasMultipleCountries = countriesList.length > 1;

  return (
    <>
      {hasMultipleCountries && (
        <FilterDropdown
          title="Country Select"
          placement="top-start"
          items={countriesList}
          disallowEmptySelection
          renderCustomTrigger={(isOpen) => (
            <SuggestedSelectTrigger
              title="Country:"
              value={selectedCountryItem ? `${selectedCountryItem.name}` : 'Select'}
              isOpen={isOpen}
            />
          )}
          selectedKeys={selectedCountryIso2 ? [selectedCountryIso2] : []}
          mode="single"
          onSelectedKeysChange={handleCountrySelect}
        />
      )}
      <AddressSearch
        addressList={addressList}
        onAddressSelect={handleAddressSelect}
        onAddressInputChange={handleAddressInputChange}
        value={addressSearchValue}
        inputProps={{
          placeholder: 'Search address',
          'aria-label': 'Address *',
          roundedClassName: 'rounded-full',
        }}
        wrapperProps={{
          'aria-label': 'Search Address',
        }}
      />
    </>
  );
};
